import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { getTokenSupply, getWithdrawOptions } from './assets'
import { getFormFieldsForWithdrawDialog, getWithdrawDialogFormValidator } from './form'
import { createWithdrawObjectives } from './objectives'
import { PositionOverview } from './types'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useSnapshot } from '@/domain/hooks/useSnapshot'
import { Token } from '@/domain/types/Token'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema, useDebouncedDialogFormValues } from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useAaveDataLayer } from '@/features/sparklend/common/data/market-info/aave-data-layer/useAaveDataLayer'
import { EPOCH_LENGTH } from '@/features/sparklend/common/data/market-info/consts'
import { updatePositionSummary } from '@/features/sparklend/common/data/market-info/updatePositionSummary'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { useMarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { RiskAcknowledgementInfo } from '@/features/sparklend/common/utils/liquidation-risk-warning/types'
import { useLiquidationRiskWarning } from '@/features/sparklend/common/utils/liquidation-risk-warning/useLiquidationRiskWarning'

export interface UseWithdrawDialogOptions {
  initialToken: Token
}

export interface UseWithdrawDialogResult {
  withdrawOptions: TokenWithBalance[]
  assetsToWithdrawFields: FormFieldsForDialog
  withdrawAsset: TokenWithValue
  actionsViewState: ActionsViewState
  pageStatus: PageStatus
  form: UseFormReturn<AssetInputSchema>
  currentPositionOverview: PositionOverview
  updatedPositionOverview?: PositionOverview
  riskAcknowledgement: RiskAcknowledgementInfo
}

export function useWithdrawDialog({ initialToken }: UseWithdrawDialogOptions): UseWithdrawDialogResult {
  const chainId = useChainId()
  const { aaveData } = useAaveDataLayer({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })
  const marketToken = marketInfo.findOneReserveBySymbol(initialToken.symbol).token
  const { marketInfo: marketInfoIn1Epoch } = useMarketInfo({ timeAdvance: EPOCH_LENGTH, chainId })
  const walletInfo = useMarketWalletInfo({ chainId })
  const nativeAssetInfo = getNativeAssetInfo(marketInfo.chainId)

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(getWithdrawDialogFormValidator({ marketInfo, aaveData, nativeAssetInfo })),
    defaultValues: {
      symbol: initialToken.symbol,
      value: '',
    },
    mode: 'onChange',
  })

  const withdrawOptions = getWithdrawOptions({
    token: marketToken,
    marketInfo,
    walletInfo,
    nativeAssetInfo,
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedDialogFormValues({
    form,
    marketInfo,
  })
  const [withdrawAsset, refreshWithdrawAsset] = useSnapshot(formValues)

  const assetsToWithdrawFields = getFormFieldsForWithdrawDialog(form, marketInfo, walletInfo)

  const updatedTokenSupply = getTokenSupply(marketInfo, formValues)

  const objectives = createWithdrawObjectives({
    formValues,
    marketInfoIn1Epoch,
  })

  const currentPositionOverview = {
    healthFactor: marketInfo.userPositionSummary.healthFactor,
    tokenSupply: formValues.position.collateralBalance,
    supplyAPY: formValues.reserve.supplyAPY,
  }
  const updatedUserSummary = updatePositionSummary({
    withdrawals: [formValues],
    marketInfo,
    aaveData,
    nativeAssetInfo,
  })
  const updatedPositionOverview = formValues.value.eq(0)
    ? undefined
    : {
        ...currentPositionOverview,
        healthFactor: updatedUserSummary.healthFactor,
        tokenSupply: updatedTokenSupply,
      }
  const { riskAcknowledgement, disableActionsByRisk } = useLiquidationRiskWarning({
    type: 'liquidation-warning-withdraw',
    isFormValid,
    currentHealthFactor: currentPositionOverview.healthFactor,
    updatedHealthFactor: updatedPositionOverview?.healthFactor,
  })

  const actionsEnabled = formValues.value.gt(0) && isFormValid && !isDebouncing && !disableActionsByRisk

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      marketInfo,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
    refreshWithdrawAsset()
  }, [
    formValues.token.symbol,
    formValues.value.toString(),
    formValues.isMaxSelected,
    resetActions,
    refreshWithdrawAsset,
  ])

  return {
    form,
    withdrawOptions,
    assetsToWithdrawFields,
    withdrawAsset,
    actionsViewState,
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    currentPositionOverview,
    updatedPositionOverview,
    riskAcknowledgement,
  }
}
